import { useEffect, useState } from 'react';

import { isDesktop } from '@/lib/platform';
import { events } from '@/lib/platform/generated/bindings.gen';

/**
 * Subscribe to the recording's paused state. Returns the latest `isPaused`
 * boolean (defaults to `false` — a recording starts un-paused).
 *
 * The desktop pause/resume commands broadcast `recording://paused` to every
 * window, so any surface (the HUD pill, the HUD expanded header, the main-window
 * audio surface transport) reflects the SAME paused state — pausing in one is
 * visible in all. Recording state is desktop-local; the server is never involved.
 * Mirrors `useVad`'s event-listener shape.
 */
export function useRecordingPaused(recordingId: string): boolean {
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    // Recording is desktop-only, but recents cards render on web and reach this
    // hook through useLiveRecording. The Tauri event proxy dereferences
    // window.__TAURI_INTERNALS__, which a browser does not have, so calling it
    // there throws "Cannot read properties of undefined (reading
    // 'transformCallback')" as an unhandled rejection (LUMEN-4G).
    if (!isDesktop) return;

    let unlisten: (() => void) | undefined;
    let active = true;
    events.recordingPaused
      .listen((e) => {
        if (e.payload.recordingId !== recordingId) return;
        setIsPaused(e.payload.isPaused);
      })
      .then((u) => {
        if (active) unlisten = u;
        else u();
      });
    return () => {
      active = false;
      unlisten?.();
    };
  }, [recordingId]);
  return isPaused;
}
